import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface CoreKeywordResult {
  topic: string;
  coreKeyword: string;
}

interface CoreKeywordMetadata {
  dataSource: 'database' | 'api' | 'unknown';
  lastUpdated: string | null;
  topic: string;
  locationCode: number;
  languageName: string;
}

interface SerpResult {
  position: number;
  domain: string;
  url: string;
  title: string;
}

interface SerpResponse {
  keyword: string;
  results: SerpResult[];
}

interface SerpMetadata {
  dataSource: 'database' | 'api' | 'unknown';
  lastUpdated: string | null;
  keyword: string;
  locationCode: number;
  languageName: string;
}

interface Entity {
  keyword: string;
  normalizedKeyword: string;
  searchVolume: number;
  keywordDifficulty: number;
  coreKeyword: string | null;
  sources: ('related' | 'suggestions' | 'ranked_keywords' | 'ranked_keywords_filtered')[];
  rankedFromPages: string[] | Array<{
    url: string;
    title: string;
    domain: string;
    position: number;
  }>;
  similarityScore: number;
}

interface EntityCostBreakdown {
  relatedKeywords?: number;
  suggestions?: number;
  rankedKeywords?: number;
  rankedKeywordsFiltered?: number;
  total: number;
}

interface EntityStats {
  totalEntities: number;
  fromRelated?: number;
  fromSuggestions?: number;
  fromRankedKeywords?: number;
  fromRankedKeywordsFiltered?: number;
  uniqueEntities: number;
  domainsAnalyzed?: number;
}

interface FilterDetails {
  inputTopic: string;
  discoveredCoreKeyword: string;
  additionalCoreKeywords: Array<{
    coreKeyword: string;
    bestEntity: string;
    similarityScore: number;
    entityCount: number;
  }>;
}

@Component({
  selector: 'app-topic-share',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './topic-share.html',
  styleUrl: './topic-share.css'
})
export class TopicShareComponent implements OnInit {
  topic: string = '';
  searchTopic: string = ''; // Store the topic that was used for the search (doesn't change while typing)
  loading = false;
  loadingSerp = false;
  error: string | null = null;
  result: CoreKeywordResult | null = null;
  metadata: CoreKeywordMetadata | null = null;
  
  serpResults: {
    topic: SerpResponse | null;
    coreKeyword: SerpResponse | null;
  } = { topic: null, coreKeyword: null };

  serpMetadata: {
    topic: SerpMetadata | null;
    coreKeyword: SerpMetadata | null;
  } = { topic: null, coreKeyword: null };

  // Cost tracking
  totalCost: number = 0;
  coreKeywordCost: number = 0;
  serpCost: number = 0;

  // Entity discovery
  loadingEntities: boolean = false;
  entities: Entity[] = [];
  entityCostBreakdown: EntityCostBreakdown | null = null;
  entityStats: EntityStats | null = null;
  urlsLimited: boolean = false;
  totalSerpUrls: number = 0;
  excludedUrls: Array<{ url: string; title: string; domain: string; source: 'topic' | 'coreKeyword' }> = [];
  entitiesDisplayLimit: number = 5;
  showAllEntities: boolean = false;

  // SERP results pagination
  serpDisplayLimit: number = 5;
  showAllSerpResults: { topic: boolean; coreKeyword: boolean } = { topic: false, coreKeyword: false };

  // Entity filtering
  filteredCoreKeywords: string[] = [];
  filterDetails: FilterDetails | null = null;
  loadingFilter: boolean = false;

  // Filtered entity discovery
  filteredEntities: Entity[] = [];
  loadingFilteredEntities: boolean = false;
  filteredEntityCostBreakdown: EntityCostBreakdown | null = null;
  filteredEntityStats: EntityStats | null = null;
  showAllFilteredEntities: boolean = false;
  domainTotalCounts: Record<string, number> = {}; // Total count per domain from API

  // Domain coverage
  domainCoverage: Array<{
    domain: string;
    entitiesCount: number; // Count of discovered entities this domain ranks for
    apiTotalCount?: number; // API total_count - actual keywords domain ranks for (matching filters)
    urlsCount: number;
    urls: Array<{ url: string; title: string; position: number; entityCount: number }>;
  }> = [];
  loadingDomainCoverage: boolean = false;
  totalEntities: number = 0; // Total discovered entities
  totalUrls: number = 0;

  // Helper to check if topic and core keyword are the same
  get isTopicSameAsCoreKeyword(): boolean {
    if (!this.searchTopic || !this.result?.coreKeyword) return false;
    return this.searchTopic.toLowerCase().trim() === this.result.coreKeyword.toLowerCase().trim();
  }
  
  // Defaults (for display only - backend will use these)
  readonly defaultLocationCode: number = 2840; // United States
  readonly defaultLanguageName: string = 'English';

  constructor(private http: HttpClient) {}

  ngOnInit() {}

  async discoverCoreKeyword() {
    if (!this.topic.trim()) {
      this.error = 'Please enter a topic';
      return;
    }

    // Store the topic used for this search (so it doesn't change while user types)
    this.searchTopic = this.topic.trim();
    
    this.loading = true;
    this.error = null;
    this.result = null;
    this.metadata = null;
    // Clear SERP results when starting a new discovery
    this.serpResults = { topic: null, coreKeyword: null };
    this.serpMetadata = { topic: null, coreKeyword: null };
    this.loadingSerp = false;
    this.totalCost = 0;
    this.coreKeywordCost = 0;
    this.serpCost = 0;
    // Clear entity discovery results
    this.entities = [];
    this.entityCostBreakdown = null;
    this.entityStats = null;
    this.urlsLimited = false;
    this.totalSerpUrls = 0;
    this.excludedUrls = [];
    this.entitiesDisplayLimit = 10;
    // Clear filtered entity results
    this.filteredCoreKeywords = [];
    this.filterDetails = null;
    this.filteredEntities = [];
    this.filteredEntityCostBreakdown = null;
    this.filteredEntityStats = null;
    this.showAllFilteredEntities = false;
    this.domainTotalCounts = {};
    // Clear domain coverage
    this.domainCoverage = [];
    this.totalEntities = 0;
    this.totalUrls = 0;
    this.showAllEntities = false;
    this.serpDisplayLimit = 5;
    this.showAllSerpResults = { topic: false, coreKeyword: false };

    try {
      const API_URL = environment.apiUrl + '/v1';
      
      // Backend will default locationCode and languageName
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/core-keyword`,
        {
          topic: this.searchTopic
        }
      ).toPromise();

      if (response.success) {
        this.result = response.data;
          this.metadata = response.metadata || {
          dataSource: 'unknown',
          lastUpdated: null,
          topic: this.searchTopic.toLowerCase().trim(),
          locationCode: this.defaultLocationCode,
          languageName: this.defaultLanguageName
        };
        
        // Track cost from core keyword API (only if fetched from API)
        if (this.metadata?.dataSource === 'api' && response.data?.tasks?.[0]?.cost) {
          this.coreKeywordCost = response.data.tasks[0].cost;
          this.totalCost += this.coreKeywordCost;
        }
        
        console.log(`📊 Core keyword result (source: ${this.metadata?.dataSource || 'unknown'}, cost: $${this.coreKeywordCost.toFixed(4)})`);
        
        // Automatically discover SERP results after core keyword is found
        await this.discoverSerpResults();
      } else {
        this.error = response.message || 'Failed to discover core keyword';
        this.metadata = null;
      }
    } catch (error: any) {
      console.error('Error:', error);
      this.error = error.error?.message || error.message || 'Failed to discover core keyword';
      this.metadata = null;
    } finally {
      this.loading = false;
    }
  }

  async discoverSerpResults() {
    if (!this.result || !this.result.coreKeyword) {
      return;
    }

    this.loadingSerp = true;
    const API_URL = environment.apiUrl + '/v1';

    // Normalize both topic and core keyword for comparison
    const normalizedTopic = this.searchTopic.toLowerCase().trim();
    const normalizedCoreKeyword = this.result.coreKeyword.toLowerCase().trim();
    const isSame = normalizedTopic === normalizedCoreKeyword;

    try {
      if (isSame) {
        // Make only one API call and reuse the result
        console.log(`📊 Topic and core keyword are the same, making single SERP call`);
        
        const response = await this.http.post<any>(
          `${API_URL}/dataforseo/topic-share/serp`,
          {
            keyword: this.searchTopic
          }
        ).toPromise();

        if (response.success) {
          const serpResponse: SerpResponse = {
            keyword: response.data.keyword,
            results: response.data.results
          };
          const serpMeta: SerpMetadata = response.metadata || {
            dataSource: 'unknown',
            lastUpdated: null,
            keyword: response.data.keyword,
            locationCode: this.defaultLocationCode,
            languageName: this.defaultLanguageName
          };

          // Track cost from SERP API (only if fetched from API)
          if (serpMeta.dataSource === 'api' && response.data?.tasks?.[0]?.cost) {
            this.serpCost = response.data.tasks[0].cost;
            this.totalCost += this.serpCost;
          }

          // Store in both slots
          this.serpResults.topic = serpResponse;
          this.serpResults.coreKeyword = serpResponse;
          this.serpMetadata.topic = serpMeta;
          this.serpMetadata.coreKeyword = serpMeta;
          
          console.log(`📊 SERP results (source: ${serpMeta.dataSource || 'unknown'}, cost: $${this.serpCost.toFixed(4)})`);
        }
      } else {
        // Make two separate API calls
        console.log(`📊 Topic and core keyword differ, making two SERP calls`);
        
        const [topicResponse, coreKeywordResponse] = await Promise.allSettled([
          this.http.post<any>(
            `${API_URL}/dataforseo/topic-share/serp`,
            { keyword: this.searchTopic }
          ).toPromise(),
          this.http.post<any>(
            `${API_URL}/dataforseo/topic-share/serp`,
            { keyword: this.result.coreKeyword }
          ).toPromise()
        ]);

        // Handle topic SERP results
        if (topicResponse.status === 'fulfilled' && topicResponse.value.success) {
          this.serpResults.topic = {
            keyword: topicResponse.value.data.keyword,
            results: topicResponse.value.data.results
          };
          this.serpMetadata.topic = topicResponse.value.metadata || {
            dataSource: 'unknown',
            lastUpdated: null,
            keyword: topicResponse.value.data.keyword,
            locationCode: this.defaultLocationCode,
            languageName: this.defaultLanguageName
          };
          
          // Track cost from topic SERP API (only if fetched from API)
          if (this.serpMetadata.topic?.dataSource === 'api' && topicResponse.value.data?.tasks?.[0]?.cost) {
            const topicCost = topicResponse.value.data.tasks[0].cost;
            this.serpCost += topicCost;
            this.totalCost += topicCost;
          }
          
          console.log(`📊 Topic SERP results (source: ${this.serpMetadata.topic?.dataSource || 'unknown'})`);
        } else {
          console.error('Failed to fetch topic SERP results:', topicResponse.status === 'rejected' ? topicResponse.reason : 'Unknown error');
        }

        // Handle core keyword SERP results
        if (coreKeywordResponse.status === 'fulfilled' && coreKeywordResponse.value.success) {
          this.serpResults.coreKeyword = {
            keyword: coreKeywordResponse.value.data.keyword,
            results: coreKeywordResponse.value.data.results
          };
          this.serpMetadata.coreKeyword = coreKeywordResponse.value.metadata || {
            dataSource: 'unknown',
            lastUpdated: null,
            keyword: coreKeywordResponse.value.data.keyword,
            locationCode: this.defaultLocationCode,
            languageName: this.defaultLanguageName
          };
          
          // Track cost from core keyword SERP API (only if fetched from API)
          if (this.serpMetadata.coreKeyword?.dataSource === 'api' && coreKeywordResponse.value.data?.tasks?.[0]?.cost) {
            const coreKeywordSerpCost = coreKeywordResponse.value.data.tasks[0].cost;
            this.serpCost += coreKeywordSerpCost;
            this.totalCost += coreKeywordSerpCost;
          }
          
          console.log(`📊 Core keyword SERP results (source: ${this.serpMetadata.coreKeyword?.dataSource || 'unknown'})`);
        } else {
          console.error('Failed to fetch core keyword SERP results:', coreKeywordResponse.status === 'rejected' ? coreKeywordResponse.reason : 'Unknown error');
        }
      }
    } catch (error: any) {
      console.error('Error fetching SERP results:', error);
      // Don't set error state - allow partial results to display
    } finally {
      this.loadingSerp = false;
    }
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  }

  // Get all unique URLs from SERP results (limited to 10)
  getAllSerpUrls(): { 
    urls: string[], 
    limited: boolean, 
    total: number,
    excluded: Array<{ url: string; title: string; domain: string; source: 'topic' | 'coreKeyword' }>
  } {
    const urls = new Set<string>();
    const includedResults: Array<{ url: string; title: string; domain: string; source: 'topic' | 'coreKeyword' }> = [];
    const excludedResults: Array<{ url: string; title: string; domain: string; source: 'topic' | 'coreKeyword' }> = [];
    
    // First, process topic results (prioritize these)
    if (this.serpResults.topic?.results) {
      for (const result of this.serpResults.topic.results) {
        if (urls.size >= 10) {
          excludedResults.push({
            url: result.url,
            title: result.title,
            domain: result.domain,
            source: 'topic'
          });
        } else {
          urls.add(result.url);
          includedResults.push({
            url: result.url,
            title: result.title,
            domain: result.domain,
            source: 'topic'
          });
        }
      }
    }
    
    // Then, process core keyword results if we haven't reached 10 yet
    if (this.serpResults.coreKeyword?.results && !this.isTopicSameAsCoreKeyword) {
      for (const result of this.serpResults.coreKeyword.results) {
        if (urls.has(result.url)) {
          // Skip if already included from topic results
          continue;
        }
        if (urls.size >= 10) {
          excludedResults.push({
            url: result.url,
            title: result.title,
            domain: result.domain,
            source: 'coreKeyword'
          });
        } else {
          urls.add(result.url);
          includedResults.push({
            url: result.url,
            title: result.title,
            domain: result.domain,
            source: 'coreKeyword'
          });
        }
      }
    }
    
    const collectedUrls = Array.from(urls);
    const totalAvailable = includedResults.length + excludedResults.length;
    const wasLimited = excludedResults.length > 0;
    
    return {
      urls: collectedUrls,
      limited: wasLimited,
      total: totalAvailable,
      excluded: excludedResults
    };
  }

  // Check if we have SERP results to discover entities from
  get hasSerpResults(): boolean {
    return !!(this.serpResults.topic?.results?.length || this.serpResults.coreKeyword?.results?.length);
  }

  async discoverEntities() {
    if (!this.hasSerpResults || !this.searchTopic || !this.result?.coreKeyword) {
      this.error = 'Please discover SERP results and core keyword first';
      return;
    }

    this.loadingEntities = true;
    this.error = null;
    const API_URL = environment.apiUrl + '/v1';

    try {
      const urlData = this.getAllSerpUrls();
      const serpPageUrls = urlData.urls;
      this.urlsLimited = urlData.limited;
      this.totalSerpUrls = urlData.total;
      this.excludedUrls = urlData.excluded;
      
      console.log('📊 SERP results state:', {
        hasTopicResults: !!this.serpResults.topic?.results,
        topicResultsLength: this.serpResults.topic?.results?.length || 0,
        hasCoreKeywordResults: !!this.serpResults.coreKeyword?.results,
        coreKeywordResultsLength: this.serpResults.coreKeyword?.results?.length || 0,
        isTopicSameAsCoreKeyword: this.isTopicSameAsCoreKeyword,
        collectedUrls: serpPageUrls,
        collectedUrlsLength: serpPageUrls.length,
        totalAvailable: urlData.total,
        wasLimited: urlData.limited,
        searchTopic: this.searchTopic
      });
      
      if (serpPageUrls.length === 0) {
        this.error = 'No SERP pages available for entity discovery. Please ensure SERP results are loaded.';
        this.loadingEntities = false;
        return;
      }

      if (!this.searchTopic || this.searchTopic.trim().length === 0) {
        this.error = 'Topic is required for entity discovery';
        this.loadingEntities = false;
        return;
      }

      if (!this.result?.coreKeyword) {
        this.error = 'Core keyword is required for entity discovery';
        this.loadingEntities = false;
        return;
      }

      console.log(`📊 Discovering entities from ${serpPageUrls.length} SERP pages...`);
      console.log('📊 Request payload:', {
        topic: this.searchTopic,
        coreKeyword: this.result.coreKeyword,
        serpPageUrls: serpPageUrls,
        serpPageUrlsLength: serpPageUrls.length
      });

      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/discover-entities`,
        {
          topic: this.searchTopic,
          coreKeyword: this.result.coreKeyword,
          serpPageUrls: serpPageUrls
        }
      ).toPromise();

      if (response.success) {
        this.entities = response.data.entities || [];
        this.entityCostBreakdown = response.data.costBreakdown || null;
        this.entityStats = response.data.stats || null;

        // Add entity discovery cost to total
        if (this.entityCostBreakdown) {
          this.totalCost += this.entityCostBreakdown.total;
        }

        console.log(`✅ Discovered ${this.entities.length} entities (cost: $${this.entityCostBreakdown?.total.toFixed(4) || '0.0000'})`);
      } else {
        this.error = response.message || 'Failed to discover entities';
      }
    } catch (error: any) {
      console.error('Error discovering entities:', error);
      console.error('Error details:', {
        status: error.status,
        statusText: error.statusText,
        error: error.error,
        message: error.message,
        url: error.url
      });
      this.error = error.error?.message || error.message || `Failed to discover entities (${error.status || 'unknown error'})`;
    } finally {
      this.loadingEntities = false;
    }
  }

  async filterEntities() {
    if (!this.entities.length) {
      this.error = 'Please discover entities first';
      return;
    }

    if (!this.searchTopic || !this.result?.coreKeyword) {
      this.error = 'Topic and core keyword are required for filtering';
      return;
    }

    this.loadingFilter = true;
    this.error = null;
    const API_URL = environment.apiUrl + '/v1';

    try {
      console.log(`🔍 Filtering ${this.entities.length} entities to core keywords...`);

      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/filter-entities`,
        {
          topic: this.searchTopic,
          coreKeyword: this.result.coreKeyword,
          entities: this.entities
        }
      ).toPromise();

      if (response.success) {
        this.filteredCoreKeywords = response.data.filteredCoreKeywords || [];
        this.filterDetails = response.data.selectionDetails || null;

        console.log(`✅ Filtered to ${this.filteredCoreKeywords.length} core keywords:`, this.filteredCoreKeywords);
      } else {
        this.error = response.message || 'Failed to filter entities';
      }
    } catch (error: any) {
      console.error('Error filtering entities:', error);
      this.error = error.error?.message || error.message || `Failed to filter entities (${error.status || 'unknown error'})`;
    } finally {
      this.loadingFilter = false;
    }
  }

  // Get unique domains from SERP results
  getUniqueDomains(): string[] {
    const domainSet = new Set<string>();
    
    // Extract domains from topic SERP results
    if (this.serpResults.topic?.results) {
      this.serpResults.topic.results.forEach(result => {
        const domain = result.domain.toLowerCase().trim().replace(/^www\./, '');
        domainSet.add(domain);
      });
    }
    
    // Extract domains from core keyword SERP results
    if (this.serpResults.coreKeyword?.results) {
      this.serpResults.coreKeyword.results.forEach(result => {
        const domain = result.domain.toLowerCase().trim().replace(/^www\./, '');
        domainSet.add(domain);
      });
    }
    
    return Array.from(domainSet);
  }

  async discoverFilteredEntities() {
    if (!this.filteredCoreKeywords.length) {
      this.error = 'Please filter entities first';
      return;
    }

    const domains = this.getUniqueDomains();
    
    if (domains.length === 0) {
      this.error = 'No domains available from SERP results';
      return;
    }

    this.loadingFilteredEntities = true;
    this.error = null;
    const API_URL = environment.apiUrl + '/v1';

    try {
      console.log(`🔍 Discovering filtered entities for ${domains.length} domains using ${this.filteredCoreKeywords.length} core keywords...`);

      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/discover-entities-filtered`,
        {
          domains: domains,
          coreKeywords: this.filteredCoreKeywords
        }
      ).toPromise();

      if (response.success) {
        this.filteredEntities = response.data.entities || [];
        this.filteredEntityCostBreakdown = response.data.costBreakdown || null;
        this.filteredEntityStats = response.data.stats || null;
        this.domainTotalCounts = response.metadata?.domainTotalCounts || {};

        // Add filtered entity discovery cost to total
        if (this.filteredEntityCostBreakdown) {
          this.totalCost += this.filteredEntityCostBreakdown.total;
        }

        console.log(`✅ Discovered ${this.filteredEntities.length} filtered entities (cost: $${this.filteredEntityCostBreakdown?.total.toFixed(4) || '0.0000'})`);
        console.log('📊 Domain total counts:', this.domainTotalCounts);
      } else {
        this.error = response.message || 'Failed to discover filtered entities';
      }
    } catch (error: any) {
      console.error('Error discovering filtered entities:', error);
      this.error = error.error?.message || error.message || `Failed to discover filtered entities (${error.status || 'unknown error'})`;
    } finally {
      this.loadingFilteredEntities = false;
    }
  }

  async getDomainCoverage() {
    // Merge initial entities (Step 4) with filtered entities (Step 6)
    const allEntities = [...this.entities, ...this.filteredEntities];
    
    if (allEntities.length === 0) {
      this.error = 'Please discover entities first';
      return;
    }

    this.loadingDomainCoverage = true;
    this.error = null;
    const API_URL = environment.apiUrl + '/v1';

    try {
      console.log(`📊 Calculating domain coverage for ${allEntities.length} entities (${this.entities.length} initial + ${this.filteredEntities.length} filtered)...`);

      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/domain-coverage`,
        {
          entities: allEntities
        }
      ).toPromise();

      if (response.success) {
        // Merge API total_count data into domain coverage
        const coverageWithApiCounts = (response.data.domains || []).map((domain: any) => ({
          ...domain,
          apiTotalCount: this.domainTotalCounts[domain.domain] || undefined
        }));
        
        this.domainCoverage = coverageWithApiCounts;
        this.totalEntities = response.data.totalEntities || 0;
        this.totalUrls = response.data.totalUrls || 0;

        console.log(`✅ Calculated coverage for ${this.domainCoverage.length} domains`);
      } else {
        this.error = response.message || 'Failed to calculate domain coverage';
      }
    } catch (error: any) {
      console.error('Error calculating domain coverage:', error);
      this.error = error.error?.message || error.message || `Failed to calculate domain coverage (${error.status || 'unknown error'})`;
    } finally {
      this.loadingDomainCoverage = false;
    }
  }

  // Get displayed filtered entities (limited)
  getDisplayedFilteredEntities(): Entity[] {
    return this.showAllFilteredEntities 
      ? this.filteredEntities 
      : this.filteredEntities.slice(0, this.entitiesDisplayLimit);
  }

  // Toggle show all filtered entities
  toggleShowAllFilteredEntities() {
    this.showAllFilteredEntities = !this.showAllFilteredEntities;
  }

  // Check if there are more filtered entities to show
  hasMoreFilteredEntities(): boolean {
    return this.filteredEntities.length > this.entitiesDisplayLimit;
  }

  // Helper to check if rankedFromPages is an array of objects
  isRankedFromPagesArray(pages: string[] | Array<{ url: string; title: string; domain: string; position: number }>): boolean {
    if (!Array.isArray(pages) || pages.length === 0) return false;
    return typeof pages[0] === 'object' && 'url' in pages[0];
  }

  // Helper to get rankedFromPages as array of page objects
  getRankedFromPagesAsObjects(pages: string[] | Array<{ url: string; title: string; domain: string; position: number }>): Array<{ url: string; title: string; domain: string; position: number }> {
    if (this.isRankedFromPagesArray(pages)) {
      return pages as Array<{ url: string; title: string; domain: string; position: number }>;
    }
    return [];
  }

  // Helper to get rankedFromPages as array of strings
  getRankedFromPagesAsStrings(pages: string[] | Array<{ url: string; title: string; domain: string; position: number }>): string[] {
    if (!this.isRankedFromPagesArray(pages)) {
      return pages as string[];
    }
    return [];
  }

  // Helper to check if value is an array (for template use)
  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  // Helper to check if rankedFromPages is empty
  isRankedFromPagesEmpty(pages: string[] | Array<{ url: string; title: string; domain: string; position: number }> | undefined): boolean {
    if (!pages) return true;
    if (!Array.isArray(pages)) return true;
    return pages.length === 0;
  }

  // Get domain counts as list for display
  getDomainCountsList(): Array<{ domain: string; totalCount: number }> {
    return Object.entries(this.domainTotalCounts)
      .map(([domain, totalCount]) => ({ domain, totalCount }))
      .sort((a, b) => b.totalCount - a.totalCount);
  }

  // Helper to check if domainTotalCounts has any entries
  hasDomainTotalCounts(): boolean {
    return Object.keys(this.domainTotalCounts).length > 0;
  }

  // Helper to format similarity score as percentage
  formatSimilarity(score: number): string {
    return `${(score * 100).toFixed(1)}%`;
  }

  // Helper to get similarity color class
  getSimilarityColorClass(score: number): string {
    if (score >= 0.7) return 'similarity-high';
    if (score >= 0.5) return 'similarity-medium';
    return 'similarity-low';
  }

  // Helper to format sources as badges
  formatSources(sources: ('related' | 'suggestions' | 'ranked_keywords' | 'ranked_keywords_filtered')[]): string {
    return sources.join(', ');
  }

  // Get displayed SERP results (limited)
  getDisplayedSerpResults(source: 'topic' | 'coreKeyword'): SerpResult[] {
    const results = source === 'topic' 
      ? this.serpResults.topic?.results || []
      : this.serpResults.coreKeyword?.results || [];
    
    const showAll = this.showAllSerpResults[source];
    return showAll ? results : results.slice(0, this.serpDisplayLimit);
  }

  // Check if there are more SERP results to show
  hasMoreSerpResults(source: 'topic' | 'coreKeyword'): boolean {
    const results = source === 'topic' 
      ? this.serpResults.topic?.results || []
      : this.serpResults.coreKeyword?.results || [];
    return results.length > this.serpDisplayLimit;
  }

  // Toggle show all SERP results
  toggleShowAllSerpResults(source: 'topic' | 'coreKeyword') {
    this.showAllSerpResults[source] = !this.showAllSerpResults[source];
  }

  // Get displayed entities (limited)
  getDisplayedEntities(): Entity[] {
    return this.showAllEntities 
      ? this.entities 
      : this.entities.slice(0, this.entitiesDisplayLimit);
  }

  // Helper to get similarity score for additional keywords
  getAdditionalKeywordSimilarity(keyword: string): number {
    if (!this.filterDetails) return 0;
    
    const detail = this.filterDetails.additionalCoreKeywords.find(
      d => d.coreKeyword.toLowerCase() === keyword.toLowerCase()
    );
    
    return detail ? detail.similarityScore : 0;
  }

  // Check if there are more entities to show
  hasMoreEntities(): boolean {
    return this.entities.length > this.entitiesDisplayLimit;
  }

  // Toggle show all entities
  toggleShowAllEntities() {
    this.showAllEntities = !this.showAllEntities;
  }
}

